/**
 * mSTEM SmartHome "Dashboard giám sát thiết bị" — presentational 3-panel
 * modal (Phase 5 / SMH-02, DR-04). Pure dumb component:
 *   - Panel A (full width): live sensor sparkline (inline SVG) + latest caption
 *   - Panel B (bottom-left): RFID swipe log via MstemTable (3 cols)
 *   - Panel C (bottom-right): threshold alerts via MstemBadge danger/warning
 *
 * The 2s tick + interval lifecycle live in the container
 * (containers/smarthome-dashboard-modal.jsx). This component only renders
 * the points array + static seed data it is handed. NOC-navy header band,
 * white body, single "Đóng" close (the modal × also closes). All strings
 * via gen.smarthome.dashboard.* so `npm run i18n:src` extracts them.
 */
import PropTypes from 'prop-types';
import React from 'react';
import {defineMessages, FormattedMessage, injectIntl, intlShape} from 'react-intl';

import Modal from '../../containers/modal.jsx';
import MstemTable from '../mstem-table/mstem-table.jsx';
import MstemBadge from '../mstem-badge/mstem-badge.jsx';
import SensorSparkline from './sensor-sparkline.jsx';

import styles from './smarthome-dashboard-modal.css';

const messages = defineMessages({
    title: {
        defaultMessage: 'Dashboard giám sát thiết bị',
        description: 'Title of the SmartHome monitoring dashboard modal (SMH-02)',
        id: 'gen.smarthome.dashboard.title'
    },
    chartTitle: {
        defaultMessage: 'Biểu đồ cảm biến (mô phỏng)',
        description: 'Dashboard chart panel head',
        id: 'gen.smarthome.dashboard.chartTitle'
    },
    latest: {
        defaultMessage: 'Giá trị hiện tại: {value} °C',
        description: 'Dashboard chart latest-value caption',
        id: 'gen.smarthome.dashboard.latest'
    },
    rfidTitle: {
        defaultMessage: 'Nhật ký quẹt thẻ RFID',
        description: 'Dashboard RFID log panel head',
        id: 'gen.smarthome.dashboard.rfidTitle'
    },
    colTime: {
        defaultMessage: 'Thời gian',
        description: 'Dashboard RFID table column: time',
        id: 'gen.smarthome.dashboard.colTime'
    },
    colUid: {
        defaultMessage: 'UID thẻ',
        description: 'Dashboard RFID table column: card UID',
        id: 'gen.smarthome.dashboard.colUid'
    },
    colResult: {
        defaultMessage: 'Kết quả',
        description: 'Dashboard RFID table column: result',
        id: 'gen.smarthome.dashboard.colResult'
    },
    allow: {
        defaultMessage: 'Cho phép',
        description: 'Dashboard RFID result: access allowed',
        id: 'gen.smarthome.dashboard.allow'
    },
    deny: {
        defaultMessage: 'Từ chối',
        description: 'Dashboard RFID result: access denied',
        id: 'gen.smarthome.dashboard.deny'
    },
    alertsTitle: {
        defaultMessage: 'Cảnh báo vượt ngưỡng',
        description: 'Dashboard threshold-alerts panel head',
        id: 'gen.smarthome.dashboard.alertsTitle'
    },
    rfidEmpty: {
        defaultMessage: 'Chưa có lượt quẹt thẻ nào',
        description: 'Dashboard RFID table empty state',
        id: 'gen.smarthome.dashboard.rfidEmpty'
    },
    alertsEmpty: {
        defaultMessage: 'Không có cảnh báo nào',
        description: 'Dashboard alerts panel empty state',
        id: 'gen.smarthome.dashboard.alertsEmpty'
    },
    close: {
        defaultMessage: 'Đóng',
        description: 'Dashboard close button',
        id: 'gen.smarthome.dashboard.close'
    }
});

// MstemTable calls col.render(row) with the row object (verified mstem-table.jsx).
const renderResult = row => (
    row.result === 'allow' ? (
        <MstemBadge variant="success">
            <FormattedMessage {...messages.allow} />
        </MstemBadge>
    ) : (
        <MstemBadge variant="danger">
            <FormattedMessage {...messages.deny} />
        </MstemBadge>
    )
);

const SmartHomeDashboardModalComponent = ({intl, series, latest, rfidLogs, alerts, onRequestClose}) => {
    const columns = [
        {key: 'time', label: <FormattedMessage {...messages.colTime} />, width: '30%'},
        {key: 'uid', label: <FormattedMessage {...messages.colUid} />},
        {key: 'result', label: <FormattedMessage {...messages.colResult} />, render: renderResult}
    ];
    return (
        <Modal
            className={styles.modalContent}
            contentLabel={intl.formatMessage(messages.title)}
            headerClassName={styles.header}
            id="smartHomeDashboardModal"
            onRequestClose={onRequestClose}
        >
            <div className={styles.body}>
                {/* Panel A — full-width sensor sparkline */}
                <div className={styles.panel}>
                    <p className={styles.sectionHead}>
                        <FormattedMessage {...messages.chartTitle} />
                    </p>
                    <SensorSparkline
                        series={series}
                        unit="°C"
                    />
                    <p className={styles.latest}>
                        <FormattedMessage
                            {...messages.latest}
                            values={{value: <span className={styles.latestValue}>{latest}</span>}}
                        />
                    </p>
                </div>

                {/* Panels B + C — 2-col row */}
                <div className={styles.panelRow}>
                    <div className={styles.panelCol}>
                        <p className={styles.sectionHead}>
                            <FormattedMessage {...messages.rfidTitle} />
                        </p>
                        <MstemTable
                            columns={columns}
                            data={rfidLogs}
                            emptyMessage={intl.formatMessage(messages.rfidEmpty)}
                        />
                    </div>
                    <div className={styles.panelCol}>
                        <p className={styles.sectionHead}>
                            <FormattedMessage {...messages.alertsTitle} />
                        </p>
                        {alerts && alerts.length ? (
                            <ul className={styles.alertList}>
                                {alerts.map((alert, idx) => (
                                    <li
                                        key={idx}
                                        className={styles.alertRow}
                                    >
                                        <MstemBadge variant={alert.severity}>
                                            {alert.severity === 'danger' ? 'Nguy hiểm' : 'Cảnh báo'}
                                        </MstemBadge>
                                        <span className={styles.alertText}>{alert.text}</span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className={styles.alertsEmpty}>
                                <FormattedMessage {...messages.alertsEmpty} />
                            </p>
                        )}
                    </div>
                </div>

                <div className={styles.footer}>
                    <button
                        type="button"
                        className={styles.closeButton}
                        onClick={onRequestClose}
                    >
                        <FormattedMessage {...messages.close} />
                    </button>
                </div>
            </div>
        </Modal>
    );
};

SmartHomeDashboardModalComponent.propTypes = {
    alerts: PropTypes.arrayOf(PropTypes.shape({
        severity: PropTypes.oneOf(['danger', 'warning']),
        text: PropTypes.string
    })),
    intl: intlShape.isRequired,
    latest: PropTypes.number,
    onRequestClose: PropTypes.func.isRequired,
    rfidLogs: PropTypes.arrayOf(PropTypes.object),
    series: PropTypes.arrayOf(PropTypes.number).isRequired
};

SmartHomeDashboardModalComponent.defaultProps = {
    alerts: [],
    rfidLogs: []
};

export default injectIntl(SmartHomeDashboardModalComponent);
